import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost } from './client'

/* ---------------------------------------------------------------------------
 * Remote Command Prompt - /api/v2/devices/{id}/cmd si /commands
 *
 * Filele vin de la SERVER (`panes`), nu din browser: cine n-are dreptul de
 * Power Shell nu primeste fila, iar daca o cere totusi cu un POST facut de mana
 * capul o refuza cu 403. Vezi App\Devices\Cmd\CmdPanes.
 * ------------------------------------------------------------------------- */

export type CmdPaneKey = 'lite' | 'terminal' | 'prompt' | 'powershell' | 'commands'

export interface CmdPane {
  key: CmdPaneKey
  label: string
  icon: string
  shell: string
  /** Fila are coloana de comenzi in dreapta (Lite si Terminal). */
  sections: boolean
  /** Omul poate chiar TRIMITE din ea, nu doar deschide. */
  can_send: boolean
}

export interface CmdCommand {
  display_name: string
  cmd_id?: number
  command?: string
  has_parameters?: number
  required?: number
  hint?: string
  placeholder?: string
  tone?: string
  info?: string
}

export interface CmdSection {
  id: string
  category_name: string
  icon?: string
  note?: string
  open?: boolean
  commands: CmdCommand[]
}

export interface DeviceCmdPage {
  device: { id: number }
  available: boolean
  asleep: boolean
  linux: boolean
  is_linux: boolean
  os_family: string
  panes: CmdPane[]
  sections: CmdSection[]
  sections_note: string
  commands_href: string
}

export function cmdPageQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'cmd'],
    queryFn: () => apiGet<DeviceCmdPage>(`/devices/${id}/cmd`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

export interface CmdSendAnswer {
  success: boolean
  msg: string
  responseDate?: string
}

export interface CmdLines {
  success: boolean
  msg: { message: string }[] | string
}

/**
 * O linie catre masina. `pane` spune din CE fila pleaca - serverul cere dreptul
 * filei aleia si tot el hotaraste shell-ul, deci nu se trimite de aici.
 */
export function sendCommand(
  id: number,
  body: { pane: CmdPaneKey; cmd: string; cmd_content?: string; arguments?: string },
) {
  return apiPost<CmdSendAnswer>(`/devices/${id}/cmd/send`, {
    device: id,
    pane: body.pane,
    cmd: body.cmd,
    ...(body.cmd_content === undefined ? {} : { cmd_content: body.cmd_content }),
    ...(body.arguments === undefined ? {} : { arguments: body.arguments }),
  })
}

export function cmdResponse(id: number, date: string) {
  return apiPost<CmdLines>(`/devices/${id}/cmd/response`, { device: id, date })
}

export function cmdHistory(id: number) {
  return apiGet<CmdLines>(`/devices/${id}/cmd/history`)
}

/** Tine masina pe pas scurt cat timp terminalul e deschis (Opteemum::watchDevice). */
export function cmdWatch(id: number) {
  return apiPost<{ ok: boolean }>(`/devices/${id}/cmd/watch`, {})
}

/* ---------------------------------------------------------------------------
 * Fila Commands - ce s-a trimis catre masina si ce a raspuns
 * ------------------------------------------------------------------------- */

export type DeviceCommandCategory = '' | 'failed' | 'succeeded' | 'sent' | 'waiting'

export interface DeviceCommandsPage {
  device: { id: number }
  categories: Record<string, string>
  active: string
}

export interface DeviceCommandRow {
  DT_RowId: string
  id: number
  kind: string
  origin: string
  date: string
  message: string
  category: string
  user: string
  has_result: number
}

export interface DeviceCommandsTable {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: DeviceCommandRow[]
  counts: Record<string, number>
}

export function deviceCommandsPageQuery(id: number) {
  return queryOptions({
    queryKey: ['device', id, 'commands'],
    queryFn: () => apiGet<DeviceCommandsPage>(`/devices/${id}/commands`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

export interface DeviceCommandsTableParams {
  start: number
  length: number
  orderColumn: number
  orderDir: 'asc' | 'desc'
  search: string
  category: DeviceCommandCategory
  from: string
  to: string
}

export function deviceCommandsTableQuery(id: number, params: DeviceCommandsTableParams) {
  const q = new URLSearchParams()
  q.set('start', String(params.start))
  q.set('length', String(params.length))
  q.set('order[0][column]', String(params.orderColumn))
  q.set('order[0][dir]', params.orderDir)
  q.set('search[value]', params.search)
  q.set('category', params.category)
  q.set('from', params.from)
  q.set('to', params.to)

  return queryOptions({
    queryKey: ['device', id, 'commands', 'table', params],
    queryFn: () => apiGet<DeviceCommandsTable>(`/devices/${id}/commands/table?${q.toString()}`),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })
}

export interface DeviceCommandResult {
  empty: boolean
  status: string
  output: string
  scan: unknown | null
}

export function commandResultQuery(id: number, commandId: number) {
  return queryOptions({
    queryKey: ['device', id, 'commands', commandId, 'result'],
    queryFn: () => apiGet<DeviceCommandResult>(`/devices/${id}/commands/${commandId}/result`),
    staleTime: 60_000,
  })
}

export function clearDeviceCommand(id: number, origin: string, commandId: number) {
  return apiPost<{ ok: boolean; error: string; gone?: boolean }>(`/devices/${id}/commands/clear`, {
    origin,
    id: commandId,
  })
}

export function resendDeviceCommand(id: number, origin: string, commandId: number) {
  return apiPost<{ ok: boolean; error: string; id: number }>(`/devices/${id}/commands/resend`, {
    origin,
    id: commandId,
  })
}
